import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import axiosRetry, { exponentialDelay } from "axios-retry";
import { z, type ZodType } from "zod";

// ─── Axios-Instanz ───────────────────────────────────────────────

const a = axios.create({
  baseURL: "/rest",
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

// ─── Retry ───────────────────────────────────────────────────────

axiosRetry(a, {
  retries: 3,
  retryDelay: exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) || axiosRetry.isSafeRequestError(error),
});

// ─── Interceptors ────────────────────────────────────────────────

a.interceptors.request.use((config) => {
  const { method, data } = config;
  if (
    (method === "post" || method === "put" || method === "patch") &&
    typeof data === "object" &&
    data !== null &&
    !(data instanceof FormData)
  ) {
    config.data = deepTrimStrings(data);
  }
  return config;
});

a.interceptors.response.use(undefined, (error) => {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    window.location.href = "/login";
    return new Promise(() => {});
  }
  return Promise.reject(error);
});

function deepTrimStrings(obj: unknown): unknown {
  if (typeof obj === "string") return obj.trim();
  if (Array.isArray(obj)) return obj.map(deepTrimStrings);
  if (typeof obj === "object" && obj !== null) {
    // Date hat keine enumerierbaren Keys → Object.entries würde {} erzeugen und
    // bricht Backend-Deserialisierung (z. B. OffsetDateTime erwartet String/Zahl).
    if (obj instanceof Date) return obj;
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, deepTrimStrings(v)]),
    );
  }
  return obj;
}

// ─── Validierung ─────────────────────────────────────────────────

const LOG_STYLE = "color: #f7bb00; font-size: 14px";

function capitalize(val: string): string {
  return `${val[0].toUpperCase()}${val.slice(1)}`;
}

function getCallerName(): string {
  const stack = new Error().stack;
  return (
    stack
      ?.split("\n")[4]
      ?.replace(/\(.*/, "")
      .trim() ?? "unknown"
  );
}

function logValidationErrors(
  issues: z.ZodIssue[],
  callerName: string,
  data: unknown,
): void {
  console.groupCollapsed("%c[schema error] %s", LOG_STYLE, callerName);

  for (const issue of issues) {
    const label = capitalize(issue.code.split("_").join(" "));
    const path = issue.path.join(".") || "(root)";

    console.groupCollapsed("%c│", LOG_STYLE, label, "→", path);
    console.error(issue.message, issue);

    if (Array.isArray(data) && typeof issue.path[0] === "number") {
      console.log("Failed list item:", data[issue.path[0]]);
    }
    console.groupEnd();
  }

  console.groupCollapsed("%c└", LOG_STYLE, "Full response data");
  console.log(data);
  console.groupEnd();

  console.groupEnd();
}

function validateHelper<S extends ZodType>(
  schema: S,
  res: { data: unknown },
): asserts res is { data: z.infer<S> } {
  const normalized =
    res.data === "" || res.data === null ? undefined : res.data;

  const result = schema.safeParse(normalized);
  if (!result.success) {
    logValidationErrors(result.error.issues, getCallerName(), normalized);
    throw result.error;
  }
  res.data = result.data;
}

// ─── Types ───────────────────────────────────────────────────────

type ApiOptions = Omit<AxiosRequestConfig, "data"> & {
  raw?: boolean;
  bodySchema?: ZodType;
};

type RawResult<S extends ZodType> = {
  data: z.infer<S>;
  responseUrl: string;
  response: AxiosResponse;
};

type ApiResult<S extends ZodType, O extends ApiOptions | undefined> =
  O extends { raw: true } ? RawResult<S> : z.infer<S>;

function buildRawResult<S extends ZodType>(res: AxiosResponse): RawResult<S> {
  return {
    data: res.data as z.infer<S>,
    responseUrl:
      (res.request as XMLHttpRequest)?.responseURL ?? "",
    response: res,
  };
}

// ─── GET ─────────────────────────────────────────────────────────

async function get<
  S extends z.ZodTypeAny,
  O extends ApiOptions | undefined = undefined,
>(
  url: string,
  schema: S,
  options?: O,
): Promise<ApiResult<S, O>> {
  const { raw, bodySchema: _, ...config } = options ?? {};
  const res = await a.get<unknown>(url, config);

  if (res.status === 204 || res.status === 205) {
    (res as { data: unknown }).data = undefined;
  }

  validateHelper(schema, res as { data: unknown });

  if (raw) return buildRawResult<S>(res) as ApiResult<S, O>;
  return res.data as ApiResult<S, O>;
}

// ─── POST ────────────────────────────────────────────────────────

async function post<
  S extends ZodType,
  B = unknown,
  O extends ApiOptions | undefined = undefined,
>(
  url: string,
  body: B,
  responseSchema: S,
  options?: O,
): Promise<ApiResult<S, O>> {
  const { raw, bodySchema, ...config } = options ?? {};
  const validBody = bodySchema ? bodySchema.parse(body) : body;
  const res = await a.post<unknown>(url, validBody, config);
  validateHelper(responseSchema, res);

  if (raw) return buildRawResult<S>(res) as ApiResult<S, O>;
  return res.data as ApiResult<S, O>;
}

// ─── PATCH ───────────────────────────────────────────────────────

async function patch<
  S extends ZodType,
  B = unknown,
  O extends ApiOptions | undefined = undefined,
>(
  url: string,
  body: B,
  responseSchema: S,
  options?: O,
): Promise<ApiResult<S, O>> {
  const { raw, bodySchema, ...config } = options ?? {};
  const validBody = bodySchema ? bodySchema.parse(body) : body;
  const res = await a.patch<unknown>(url, validBody, config);
  validateHelper(responseSchema, res);

  if (raw) return buildRawResult<S>(res) as ApiResult<S, O>;
  return res.data as ApiResult<S, O>;
}

// ─── DELETE ──────────────────────────────────────────────────────

async function drop<
  S extends ZodType,
  B = undefined,
  O extends ApiOptions | undefined = undefined,
>(
  url: string,
  responseSchema: S,
  options?: O & { body?: B },
): Promise<ApiResult<S, O>> {
  const { raw, bodySchema, body, ...rest } = (options ?? {}) as ApiOptions & {
    body?: B;
  };
  const validBody = bodySchema ? bodySchema.parse(body) : body;

  const cfg: AxiosRequestConfig = { ...rest };
  if (validBody !== undefined) {
    cfg.data = validBody;
  }

  const res = await a.delete<unknown>(url, cfg);
  validateHelper(responseSchema, res);

  if (raw) return buildRawResult<S>(res) as ApiResult<S, O>;
  return res.data as ApiResult<S, O>;
}

export { get, post, drop, patch };
